import { useRef, useState } from 'react'
import { FileUp, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { useGenerateEvidenceUploadUrl, useRegisterEvidence } from '@/api/bonificacao-hooks'

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/jpg', 'image/gif',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

export interface IndicatorEvidenceUploadProps {
  assessmentId: string
  indicadorId: string
  canUpload: boolean
}

export function IndicatorEvidenceUpload({
  assessmentId,
  indicadorId,
  canUpload,
}: IndicatorEvidenceUploadProps) {
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const generateUploadUrl = useGenerateEvidenceUploadUrl()
  const registerEvidence = useRegisterEvidence()

  if (!canUpload) return null

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = ''

    // Client-side validation
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use PDF, imagens (JPG/PNG), Excel ou CSV.')
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Arquivo muito grande. O tamanho máximo é 10MB.')
      return
    }

    setUploading(true)
    try {
      // Step 1: Get presigned URL
      const { signedUrl, evidenceId, s3Key } = await generateUploadUrl.mutateAsync({
        assessmentId,
        indicadorId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      })

      // Step 2: Upload directly to S3
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
          'x-amz-server-side-encryption': 'AES256',
        },
      })
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`)
      }

      // Step 3: Register evidence metadata
      await registerEvidence.mutateAsync({
        assessmentId,
        indicadorId,
        evidenceId,
        s3Key,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
      })

      // Invalidate assessment query to refresh evidence list
      await queryClient.invalidateQueries({
        queryKey: ['bonus', 'getAssessment', { assessmentId }],
      })

      toast.success('Evidência enviada com sucesso!')
    } catch (error) {
      console.error('Evidence upload failed:', error)
      toast.error('Falha ao enviar evidência. Tente novamente.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div data-testid={`evidence-upload-${indicadorId}`} className="inline-flex">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.gif,.docx,.xlsx,.xls,.csv"
        onChange={handleFileSelect}
        disabled={uploading}
      />
      <button
        type="button"
        className="btn-ghost p-2 hover:text-brand-primary transition-colors"
        disabled={uploading}
        title="Anexar evidência"
        data-testid={`evidence-upload-btn-${indicadorId}`}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <FileUp className="w-4 h-4" />
        )}
      </button>
    </div>
  )
}
